import { useState, useEffect, useRef, useContext } from 'react';
import { Badge } from '@/types';
import { useApp, AppContext } from '@/context/AppContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Trophy, Award, Crown, Star } from 'lucide-react';

interface BadgeUnlockModalProps {
  badge: Badge;
  isOpen: boolean;
  onClose: () => void;
}

function BadgeUnlockModal({ badge, isOpen, onClose }: BadgeUnlockModalProps) {
  const getRarityIcon = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return Crown;
      case 'epic': return Award;
      case 'rare': return Trophy;
      default: return Star;
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'from-yellow-400 to-amber-600';
      case 'epic': return 'from-purple-400 to-purple-600';
      case 'rare': return 'from-blue-400 to-blue-600';
      default: return 'from-green-400 to-green-600';
    }
  };

  const getRarityBorder = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'border-yellow-500 shadow-yellow-500/50';
      case 'epic': return 'border-purple-500 shadow-purple-500/50';
      case 'rare': return 'border-blue-500 shadow-blue-500/50';
      default: return 'border-green-500 shadow-green-500/50';
    }
  };

  const Icon = getRarityIcon(badge.rarity);
  const gradientColor = getRarityColor(badge.rarity);
  const borderColor = getRarityBorder(badge.rarity);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-background/95 backdrop-blur-sm border-2 overflow-hidden">
        <div className="relative p-6 text-center">
          {/* Animated background sparkles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <Sparkles
                key={i}
                className={`absolute w-4 h-4 text-yellow-400 opacity-70 animate-pulse`}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-primary mb-2 animate-fade-in">
              🎉 Badge Unlocked!
            </h2>
          </div>

          {/* Badge Display */}
          <div className="relative mb-6 animate-scale-in">
            <div className={`relative w-32 h-32 mx-auto rounded-full bg-gradient-to-br ${gradientColor} ${borderColor} border-4 shadow-2xl flex items-center justify-center animate-pulse`}>
              {/* Badge emoji/icon */}
              <div className="text-6xl mb-2">
                {badge.icon}
              </div>
              
              {/* Rarity indicator */}
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-background rounded-full border-2 border-current flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </div>
            </div>
            
            {/* Glow effect */}
            <div className={`absolute inset-0 w-32 h-32 mx-auto rounded-full bg-gradient-to-br ${gradientColor} opacity-30 blur-xl animate-pulse`} />
          </div>

          {/* Badge Info */}
          <div className="space-y-3 mb-6">
            <h3 className="text-2xl font-bold text-foreground">
              {badge.name}
            </h3>
            
            <p className="text-muted-foreground text-sm leading-relaxed">
              {badge.description}
            </p>
            
            <div className="flex items-center justify-center gap-2">
              <Icon className={`w-4 h-4 text-${badge.rarity === 'legendary' ? 'yellow' : badge.rarity === 'epic' ? 'purple' : badge.rarity === 'rare' ? 'blue' : 'green'}-500`} />
              <span className={`text-sm font-semibold capitalize text-${badge.rarity === 'legendary' ? 'yellow' : badge.rarity === 'epic' ? 'purple' : badge.rarity === 'rare' ? 'blue' : 'green'}-500`}>
                {badge.rarity} Badge
              </span>
            </div>
          </div>

          {/* Close Button */}
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold py-3 text-lg transition-all duration-200 hover:scale-105"
          >
            Awesome! 🎉
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BadgeUnlockNotifications() {
  const context = useContext(AppContext);
  
  // Return early if context is not available (component rendered outside provider)
  if (!context) {
    return null;
  }
  
  const { state, loading } = context;
  const [unlockedBadge, setUnlockedBadge] = useState<Badge | null>(null);
  const [showModal, setShowModal] = useState(false);
  const previousBadgesRef = useRef<Badge[]>([]);

  // Initialize with current badges on first render
  useEffect(() => {
    if (loading || !state.isAuthenticated) return;
    if (previousBadgesRef.current.length === 0 && state.badges) {
      previousBadgesRef.current = [...state.badges];
    }
  }, [state.badges, loading, state.isAuthenticated]);

  useEffect(() => {
    // Don't process notifications while loading or not authenticated
    if (loading || !state.isAuthenticated) return;

    const previousBadges = previousBadgesRef.current;
    const currentBadges = state.badges || [];

    console.log('🏅 Badge notification check:', {
      previousCount: previousBadges.length,
      currentCount: currentBadges.length,
      currentUnlocked: currentBadges.filter(b => b.unlockedAt).length
    });

    // Check for newly unlocked badges
    const newlyUnlocked = currentBadges.filter(currentBadge => {
      const prevBadge = previousBadges.find(prev => prev.id === currentBadge.id);
      const isNewlyUnlocked = currentBadge.unlockedAt && (!prevBadge || !prevBadge.unlockedAt);
      
      if (isNewlyUnlocked) {
        console.log(`🎉 Newly unlocked badge detected: ${currentBadge.name}`, currentBadge);
      }
      
      return isNewlyUnlocked;
    });

    // Show modal for the first newly unlocked badge
    if (newlyUnlocked.length > 0) {
      console.log(`🎉 Showing badge notification for: ${newlyUnlocked[0].name}`);
      setUnlockedBadge(newlyUnlocked[0]);
      setShowModal(true);
    }

    // Update the previous badges reference
    previousBadgesRef.current = [...currentBadges];
  }, [state.badges, loading, state.isAuthenticated]);

  const handleCloseModal = () => {
    setShowModal(false);
    setUnlockedBadge(null);
  };

  // Don't render anything if loading or not authenticated
  if (loading || !state.isAuthenticated) {
    return null;
  }

  return unlockedBadge ? (
    <BadgeUnlockModal
      badge={unlockedBadge}
      isOpen={showModal}
      onClose={handleCloseModal}
    />
  ) : null;
}